// src/App.jsx
import React, { useState, useRef, useEffect } from 'react';
import ChatHistory from './components/ChatHistory';
import MessageInput from './components/MessageInput';
import autoChooseConfig from './config/autoChooseConfig';
import defaultModuleConfig from './config/defaultModuleConfig';
import imageRecognitionConfig from './config/imageRecognitionConfig';
import webSearchConfig from './config/webSearchConfig';
import imageGenerationConfig from './config/imageGenerationConfig';
import * as pdfjsLib from 'pdfjs-dist/webpack';
import autoSuggestionConfig from './config/autoSuggestionConfig';
import Modal from './components/Modal';
import ImagePromptModal from './components/ImagePromptModal';
import ChatHistoryModal from './components/ChatHistoryModal';
import ChatPanel from './components/ChatPanel';
import SettingsPanel from './components/SettingsPanel';
import LanguageSelector from './components/LanguageSelector';
import LeftTray from './components/LeftTray';
import ConversationPanel from './components/ConversationPanel';
import './App.css';
import { getTranslation } from './utils/translations';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Login from './components/auth/Login';
import Signup from './components/auth/Signup';
import ProtectedRoute from './components/auth/ProtectedRoute';
// Import Auth.css inside the components that need it instead of globally
// import './components/auth/Auth.css';

const App = () => {
  // State variables
  const [messages, setMessages] = useState([]);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(''); // Store the uploaded image URL
  const [sendButtonVisible, setSendButtonVisible] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [pendingPdfUrl, setPendingPdfUrl] = useState(null);
  const [showImageModal, setShowImageModal] = useState(false); // Add state for image modal
  const [expertiseLevel, setExpertiseLevel] = useState(() => {
    return localStorage.getItem('expertiseLevel') || 'student';
  });
  const [showChatHistoryModal, setShowChatHistoryModal] = useState(false);
  const [savedChats, setSavedChats] = useState({});
  const [isChatPanelOpen, setIsChatPanelOpen] = useState(false);
  const [activePanelType, setActivePanelType] = useState(null); // 'chat' or 'settings' or null
  const [selectedLanguage, setSelectedLanguage] = useState(() => {
    return localStorage.getItem('selectedLanguage') || 'en';
  });
  const [isLanguageMenuOpen, setIsLanguageMenuOpen] = useState(false);
  const [selectedModel, setSelectedModel] = useState(() => {
    const savedModel = localStorage.getItem('selectedModel');
    return savedModel || 'anthropic'; // Default to 'anthropic' or 'llama'
  });
  const [selectedVoice, setSelectedVoice] = useState('en-US-AvaMultilingualNeural'); // Default to Ava
  const [searchHighlight, setSearchHighlight] = useState('');
  const [searchDirection, setSearchDirection] = useState(null);
  const [isTrayOpen, setIsTrayOpen] = useState(false);

  // Add ref for MessageInput
  const messageInputRef = useRef();

  // Load saved chats on initial render
  useEffect(() => {
    const loadedChats = localStorage.getItem('savedChats');
    if (loadedChats) {
      setSavedChats(JSON.parse(loadedChats));
    }
  }, []);

  // Auto-save chat when messages change
  useEffect(() => {
    if (messages.length > 0) {
      const firstUserMessage = messages.find(msg => msg.role === 'user');
      if (firstUserMessage) {
        let title;
        if (typeof firstUserMessage.content === 'string') {
          // Handle text content
          title = firstUserMessage.content.split(' ').slice(0, 3).join(' ');
        } else if (Array.isArray(firstUserMessage.content)) {
          // Handle array content (for messages with images/PDFs)
          const textContent = firstUserMessage.content.find(item => item.type === 'text');
          title = textContent ? textContent.text.split(' ').slice(0, 3).join(' ') : 'Untitled Chat';
        }

        const chatData = {
          messages,
          timestamp: Date.now(),
          title
        };

        setSavedChats(prevChats => {
          const newChats = {
            ...prevChats,
            [title]: chatData
          };
          localStorage.setItem('savedChats', JSON.stringify(newChats));
          return newChats;
        });
      }
    }
  }, [messages]);

  // Add this new useEffect for initial suggestions
  useEffect(() => {
    const initialSuggestions = [
      "Tell me about quantum computing",
      "What's new in AI technology?",
      "Explain blockchain in simple terms",
      "Give me a coding challenge"
    ];
    setSuggestions(initialSuggestions);
  }, []); // Empty dependency array means this runs once on mount

  // Save language preference
  useEffect(() => {
    localStorage.setItem('selectedLanguage', selectedLanguage);
  }, [selectedLanguage]);

  // Helper function to get language instruction
  const getLanguageInstruction = (lang) => {
    switch(lang) {
      case 'es':
        return 'Provide your response in Spanish';
      case 'zh':
        return 'Provide your response in Chinese';
      case 'hi':
        return 'Provide your response in Hindi';
      case 'fr':
        return 'Provide your response in French';
      case 'pt':
        return 'Provide your response in Portuguese';
      case 'ru':
        return 'Provide your response in Russian';
      case 'ko':
        return 'Provide your response in Korean';
      case 'ja':
        return 'Provide your response in Japanese';
      case 'de':
        return 'Provide your response in German';
      case 'it':
        return 'Provide your response in Italian';
      default:
        return 'Provide your response in English';
    }
  };

  // Modify the system message creation to include language
  const createSystemMessage = (expertiseLevel) => {
    return `${getLanguageInstruction(selectedLanguage)}. Provide a response at a ${expertiseLevel} reading level. think about what it means to be a ${expertiseLevel} and how that should affect the response. for example, if the user asks a question, a novice would expect a basic answer, while an expert would expect a more complex and detailed answer. If the user ever asks anything pertaining to your creation or about you, do not ever talk about claude or anthropic. You are an AI Assistant developed by Illuminant Inc. that makes use of multiple AI models to provide the best response possible for all situations.`;
  };

  // Simplified handleSend function
  const handleSend = async (userInput, webSearchEnabled = false, isRegeneration = false) => {
    console.log('handleSend called with:', { userInput, webSearchEnabled, isRegeneration });
    setSuggestions([]);
    setIsProcessing(true);

    // Helper function to strip images from messages except the last one
    const stripImagesExceptLast = (messages) => {
      let foundLastImage = false;
      return messages.reverse().map(msg => {
        if (Array.isArray(msg.content)) {
          // Keep only text content and possibly one image
          const newContent = msg.content.reduce((acc, item) => {
            if (item.type === 'text') {
              acc.push(item);
            } else if (item.type === 'image' && !foundLastImage) {
              foundLastImage = true;
              acc.push(item);
            }
            return acc;
          }, []);
          return { ...msg, content: newContent };
        }
        return msg;
      }).reverse();
    };

    // Helper function to make API call with fallback
    const makeAPICallWithFallback = async (messages, config) => {
      try {
        // Try Anthropic first
        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages,
            model: 'claude-3-5-sonnet-20241022',
            temperature: config.temperature || 0.7,
            max_tokens: config.max_tokens || 1000,
          }),
        });

        if (!response.ok) {
          throw new Error('Anthropic API failed');
        }

        const data = await response.json();
        return data;

      } catch (error) {
        console.log('Anthropic API failed, falling back to Llama 90B Vision');
        
        // Strip images except the last one for Llama
        const strippedMessages = stripImagesExceptLast(messages);

        // Try Llama 90B Vision as fallback
        const llamaResponse = await fetch('/api/chat-llama', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: strippedMessages,
            model: 'llama-3.2-90b-vision-preview',
            temperature: config.temperature || 0.7,
            max_tokens: config.max_tokens || 1000,
          }),
        });

        if (!llamaResponse.ok) {
          throw new Error('Both APIs failed');
        }

        const llamaData = await llamaResponse.json();
        return llamaData;
      }
    };

    try {
      if (isRegeneration) {
        // Find the last visible user message
        const lastUserMessageIndex = messages.findIndex((msg, index) => {
          return msg.role === 'user' && !msg.hidden && messages.slice(index + 1).some(m => m.role === 'assistant');
        });

        if (lastUserMessageIndex >= 0) {
          // Get the original user message and its web search state
          const originalMessage = messages[lastUserMessageIndex];
          const wasWebSearchEnabled = originalMessage.webSearchEnabled;

          // Remove all messages from the last user message onwards
          setMessages(prevMessages => prevMessages.slice(0, lastUserMessageIndex));

          // Re-send the original message with its original web search state
          await handleSend(
            originalMessage.content,
            wasWebSearchEnabled,
            false // Not marking as regeneration since we're already handling it
          );
        }
        return;
      }

      if (pendingPdfUrl) {
        // Handle initial PDF submission with user input
        const pdfMessageContent = [
          {
            type: 'document',
            source: {
              type: 'url',
              url: pendingPdfUrl,
            },
          },
        ];

        if (userInput && userInput.trim()) {
          pdfMessageContent.push({
            type: 'text',
            text: userInput,
          });
        }

        // Wrap content in a message object with role
        const userMessage = {
          role: 'user',
          content: pdfMessageContent,
        };

        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: [userMessage],
            model: 'claude-3-5-sonnet-20241022',
            temperature: 0.7,
            max_tokens: 1000,
            system: `${getLanguageInstruction(selectedLanguage)}. Analyze the provided PDF document.`
          }),
        });

        setPendingPdfUrl(null); // Clear the pending PDF

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Server response:', errorData);
          throw new Error(
            `Failed to get AI analysis: ${errorData.details || 'Unknown error'}`
          );
        }

        const aiResponse = await response.json();

        setMessages(prevMessages => {
          const newMessages = [
            ...prevMessages,
            userMessage,
            {
              role: 'assistant',
              content: aiResponse.choices[0].message.content,
              visible: true,
            }
          ];

          if (messageInputRef.current) {
            messageInputRef.current.processSuggestions(newMessages);
          }

          return newMessages;
        });

      } else if (uploadedImageUrl) {
        // Handle image submission
        const imageMessageContent = [
          {
            type: 'image',
            source: {
              type: 'url',
              url: uploadedImageUrl,
            },
          },
        ];

        if (userInput && userInput.trim()) {
          imageMessageContent.push({
            type: 'text',
            text: userInput,
          });
        }

        // Wrap content in a message object with role
        const userMessage = {
          role: 'user',
          content: imageMessageContent,
        };

        // Create messages array with system message
        const messagesWithSystem = [
          {
            role: 'system',
            content: `${getLanguageInstruction(selectedLanguage)}. Analyze the provided image.`
          },
          userMessage
        ];

        const response = await fetch('/api/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            messages: messagesWithSystem,
            model: 'claude-3-5-sonnet-20241022',
            temperature: 0.7,
            max_tokens: 1000,
          }),
        });

        setUploadedImageUrl(''); // Clear the uploaded image

        if (!response.ok) {
          const errorData = await response.json();
          console.error('Server response:', errorData);
          throw new Error(
            `Failed to get AI analysis: ${errorData.details || 'Unknown error'}`
          );
        }

        const aiResponse = await response.json();

        setMessages(prevMessages => {
          const newMessages = [
            ...prevMessages,
            userMessage,
            {
              role: 'assistant',
              content: aiResponse.choices[0].message.content,
              visible: true,
            }
          ];

          if (messageInputRef.current) {
            messageInputRef.current.processSuggestions(newMessages);
          }

          return newMessages;
        });
      } else {
        // Regular message handling with expertise level
        const userMessage = {
          role: 'user',
          content: userInput,
          visible: true,
          webSearchEnabled: webSearchEnabled
        };

        setMessages((prevMessages) => [...prevMessages, userMessage]);

        // Get previous messages for context, including system message
        const contextMessages = [
          {
            role: 'system',
            content: createSystemMessage(expertiseLevel)
          },
          ...messages, 
          userMessage
        ].map((msg) => ({
          role: msg.role,
          content: msg.content,
        }));

        if (webSearchEnabled) {
          // Perform web search steps

          // Step 1: Generate search query - without expertise level system message
          const queryPromptMessages = [
            {
              role: 'system',
              content: `${getLanguageInstruction(selectedLanguage)}. Generate a concise Google search query based on the conversation and the user's message. Keep it under 10 words. Provide only the query without any additional text.`
            },
            ...messages, // Use original messages without the expertise system message
            userMessage
          ];

          const queryResponse = await fetch('/api/chat', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              messages: queryPromptMessages,
              model: 'claude-3-5-sonnet-20241022',
              temperature: 0.5,
              max_tokens: 100,
            }),
          });

          if (!queryResponse.ok) {
            throw new Error('Failed to get search query from AI');
          }

          const queryData = await queryResponse.json();
          const searchQuery = queryData.choices[0].message.content.trim();

          console.log('Generated search query:', searchQuery);

          // Step 2: Perform Google Search
          const searchResponse = await fetch('/api/google-search', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ query: searchQuery }),
          });

          if (!searchResponse.ok) {
            throw new Error('Failed to perform Google Search');
          }

          const searchData = await searchResponse.json();

          // Collect search results
          const searchResults = (searchData.items || [])
            .map((item) => {
              if (item.type === 'image') {
                return {
                  type: 'image',
                  link: item.link,
                  thumbnailUrl: item.thumbnailUrl
                };
              }
              return {
                type: 'text',
                text: `${item.title}\n${item.snippet}\n${item.link}`
              };
            });

          // Create search result message with only text content
          const searchResultMessage = {
            role: 'user',
            content: `Web search results for query "${searchQuery}":\n\n${searchResults
              .filter(item => item.type === 'text')
              .map(item => item.text)
              .join('\n\n')}`,
            hidden: true
          };

          setMessages(prevMessages => [...prevMessages, searchResultMessage]);

          // Step 3: Answer user's request using search results - now include expertise level
          const answerPromptMessages = [
            {
              role: 'system',
              content: createSystemMessage(expertiseLevel)
            },
            ...messages,
            userMessage,
            searchResultMessage,
            {
              role: 'system',
              content: `${getLanguageInstruction(selectedLanguage)}. Answer the user's request as extensively as possible using these web search results and provide links to the sources.`,
            },
          ];

          try {
            const answerData = await makeAPICallWithFallback(answerPromptMessages, {
              temperature: 0.7,
              max_tokens: 1000,
            });

            const images = searchResults.filter(item => item.type === 'image');
            const aiResponseContent = answerData.choices[0].message.content.trim();

            // Add image validation function
            const validateImage = (url) => {
              return new Promise((resolve) => {
                const img = new Image();
                img.onload = () => resolve(true);
                img.onerror = () => resolve(false);
                img.src = url;
              });
            };

            // Validate images before displaying
            const validatedImagesPromises = images.map(img => 
              validateImage(img.link).then(isValid => ({
                ...img,
                isValid
              }))
            );

            const validatedImages = await Promise.all(validatedImagesPromises);
            const workingImages = validatedImages
              .filter(img => img.isValid)
              .slice(0, 4); // Take first 4 working images

            const enhancedContent = workingImages.length > 0
              ? `${aiResponseContent}

<div class="image-grid">
${workingImages
  .map(img => `<img src="${img.link}" alt="${img.title || 'Search Result'}" />`)
  .join('\n')}
</div>`
              : aiResponseContent;

            setMessages(prevMessages => {
              const newMessages = [
                ...prevMessages,
                {
                  role: 'assistant',
                  content: enhancedContent,
                  visible: true,
                }
              ];

              if (messageInputRef.current) {
                messageInputRef.current.processSuggestions(newMessages);
              }

              return newMessages;
            });
          } catch (error) {
            console.error('Error getting AI response:', error);
            throw error; // Let the outer try-catch handle this
          }
        } else {
          // Regular message handling with expertise level
          try {
            console.log('Attempting Anthropic API call...');
            if (selectedModel === 'anthropic') {
              const response = await fetch('/api/chat', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  messages: contextMessages,
                  model: 'claude-3-5-sonnet-20241022',
                  temperature: 0.7,
                  system: createSystemMessage(expertiseLevel),
                }),
              });

              if (!response.ok) {
                console.log('Anthropic API failed, attempting Llama fallback...');
                throw new Error('Anthropic API failed');
              }

              const aiResponse = await response.json();
              console.log('Anthropic API succeeded');

              const assistantMessage = {
                role: 'assistant',
                content: aiResponse.choices[0].message.content,
                visible: true,
              };

              setMessages(prevMessages => {
                const newMessages = [...prevMessages, assistantMessage];
                if (messageInputRef.current) {
                  messageInputRef.current.processSuggestions(newMessages);
                }
                return newMessages;
              });

              // Return the assistant's message for the conversation panel
              return assistantMessage;
            } else if (selectedModel === 'llama') {
              const response = await fetch('/api/chat-llama', {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                  messages: contextMessages,
                  model: 'llama-3.2-90b-vision-preview',
                  temperature: 0.7,
                  max_tokens: 1000,
                }),
              });

              if (!response.ok) {
                console.error('Llama API failed');
                throw new Error('Both APIs failed');
              }

              const llamaData = await response.json();
              console.log('Llama API succeeded');

              const assistantMessage = {
                role: 'assistant',
                content: llamaData.choices[0].message.content,
                visible: true,
              };

              setMessages(prevMessages => {
                const newMessages = [...prevMessages, assistantMessage];
                if (messageInputRef.current) {
                  messageInputRef.current.processSuggestions(newMessages);
                }
                return newMessages;
              });

              // Add this return statement
              return assistantMessage;
            }
          } catch (error) {
            console.log('Attempting Llama fallback...');
            try {
              // Strip images for Llama (it can only handle one image)
              const strippedMessages = contextMessages.map(msg => {
                if (Array.isArray(msg.content)) {
                  const textContent = msg.content
                    .filter(item => item.type === 'text')
                    .map(item => item.text)
                    .join('\n');
                  return { ...msg, content: textContent };
                }
                return msg;
              });

              if (selectedModel === 'llama') {
                const llamaResponse = await fetch('/api/chat-llama', {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                  },
                  body: JSON.stringify({
                    messages: strippedMessages,
                    model: 'llama-3.2-90b-vision-preview',
                    temperature: 0.7,
                    max_tokens: 1000,
                  }),
                });

                if (!llamaResponse.ok) {
                  console.error('Llama API failed');
                  throw new Error('Both APIs failed');
                }

                const llamaData = await llamaResponse.json();
                console.log('Llama API succeeded');

                setMessages(prevMessages => {
                  const newMessages = [
                    ...prevMessages,
                    {
                      role: 'assistant',
                      content: llamaData.choices[0].message.content,
                      visible: true,
                    }
                  ];

                  if (messageInputRef.current) {
                    messageInputRef.current.processSuggestions(newMessages);
                  }

                  return newMessages;
                });
              }
            } catch (fallbackError) {
              console.error('Both APIs failed:', fallbackError);
              setMessages(prevMessages => [
                ...prevMessages,
                {
                  role: 'assistant',
                  content: 'Sorry, both AI models failed to process your request. Please try again later.',
                  visible: true,
                },
              ]);
            }
          }
        }
      }
    } catch (error) {
      console.error('Error in handleSend:', error);
      const errorMessage = {
        role: 'assistant',
        content: 'Sorry, both AI models failed to process your request. Please try again later.',
        visible: true,
      };
      setMessages(prevMessages => [...prevMessages, errorMessage]);
      return errorMessage;
    } finally {
      setIsProcessing(false);
    }
  };

  // Simplified handleImageUpload function
  const handleImageUpload = async (imageUrl, isPdf = false) => {
    console.log('handleImageUpload called with:', { imageUrl, isPdf });
    
    if (isPdf) {
      setPendingPdfUrl(imageUrl);
    } else {
      setUploadedImageUrl(imageUrl);
    }
  };

  // Function to clear chat history
  const handleClearChat = () => {
    setMessages([]);
    setUploadedImageUrl('');
  };

  // Add handler for send button visibility
  const handleSendButtonVisibility = (isVisible) => {
    setSendButtonVisible(isVisible);
  };

  const handleImageGeneration = async (prompt) => {
    try {
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate image');
      }

      const data = await response.json();
      
      if (data.success) {
        // Add the generated image message to chat history
        setMessages(prevMessages => [...prevMessages, data.message]);
      }

      return data;
    } catch (error) {
      console.error('Error generating image:', error);
      throw error;
    }
  };

  const processModule = async (moduleConfig, userMessage, previousMessages = []) => {
    try {
      let messagesToSend = [];
      
      // Add system message if configured
      if (moduleConfig.includeSystemMessage && moduleConfig.systemMessage) {
        messagesToSend.push({
          role: 'system',
          content: moduleConfig.systemMessage
        });
      }

      // Add context if configured
      if (moduleConfig.includeContext) {
        messagesToSend = [...messagesToSend, ...previousMessages];
      }

      // Add user message
      messagesToSend.push({
        role: 'user',
        content: userMessage
      });

      // No need to strip images anymore - Claude can handle them
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: messagesToSend,
          model: moduleConfig.model,
          temperature: moduleConfig.temperature,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to get AI response');
      }

      const data = await response.json();
      return data.choices[0].message.content;
    } catch (error) {
      console.error('Error fetching AI response:', error);
      throw error;
    }
  };

  // Modify the suggestion click handler
  const handleSuggestionClick = (suggestion, isRegeneration = false) => {
    setSuggestions([]); // Clear suggestions immediately
    if (isRegeneration) {
      handleSend(suggestion, false, true); // Pass isRegeneration flag
    } else {
      // For non-regenerate suggestions, respect the current web search state
      const webSearchEnabled = document.querySelector('.web-search-label.enabled') !== null;
      handleSend(suggestion, webSearchEnabled);
    }
  };

  // Function to handle "Generate Image" button click
  const handleGenerateImageClick = () => {
    setShowImageModal(true);
  };

  // Function to handle submission of the image prompt from the modal
  const handleImagePromptSubmit = async (prompt) => {
    setShowImageModal(false);
    try {
      console.log('Generating image with prompt:', prompt);
      const response = await fetch('/api/generate-image', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ prompt }),
      });

      if (!response.ok) {
        throw new Error('Failed to generate image');
      }

      const data = await response.json();

      if (data.success) {
        // Create a message with the generated image
        const imageMessageContent = [
          {
            type: 'image',
            source: {
              type: 'url',
              url: data.imageUrl,
            },
          },
          {
            type: 'text',
            text: `Generated image for prompt: "${prompt}"`,
          },
        ];

        const userMessage = {
          role: 'user',
          content: imageMessageContent,
          visible: true,
        };

        setMessages((prevMessages) => [...prevMessages, userMessage]);
      }
    } catch (error) {
      console.error('Error generating image:', error);
      setMessages((prevMessages) => [
        ...prevMessages,
        {
          role: 'assistant',
          content: 'Sorry, there was an error generating the image.',
          visible: true,
        },
      ]);
    }
  };

  const handleExpertiseLevelChange = (newLevel) => {
    setExpertiseLevel(newLevel);
  };

  const handleShowPreviousChats = () => {
    setShowChatHistoryModal(true);
  };

  const handleLoadChat = (chatData, searchTerm) => {
    setMessages(chatData.messages);
    setSearchHighlight(searchTerm || '');
  };

  const handleDeleteChat = (title) => {
    setSavedChats(prevChats => {
      const newChats = { ...prevChats };
      delete newChats[title];
      localStorage.setItem('savedChats', JSON.stringify(newChats));
      return newChats;
    });
  };

  const handleNewChat = () => {
    setMessages([]);
    setUploadedImageUrl('');
    setIsChatPanelOpen(false);
  };

  // Update the panel toggle handlers
  const handleChatPanelToggle = () => {
    setActivePanelType(current => current === 'chat' ? null : 'chat');
    setIsTrayOpen(true);
  };

  const handleSettingsPanelToggle = () => {
    setActivePanelType(current => current === 'settings' ? null : 'settings');
  };

  // Update the outside click handler
  const handleOutsideClick = (e) => {
    if (activePanelType) {
      const chatPanel = document.querySelector('.chat-panel');
      const settingsPanel = document.querySelector('.settings-panel');
      const leftTray = document.querySelector('.left-tray');
      const conversationPanel = document.querySelector('.conversation-panel');
      const clickedPanel = e.target.closest('.chat-panel, .settings-panel, .left-tray, .conversation-panel');
      const clickedToggle = e.target.closest('.chat-panel-toggle, .settings-button');
      const clickedButton = e.target.closest('button'); // Prevent closing when clicking any button inside panels
      
      if (!clickedPanel && !clickedToggle && !clickedButton) {
        setActivePanelType(null);
        setIsTrayOpen(false);
      }
    }
  };

  // Add effect to handle outside clicks
  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick);

    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [activePanelType]); // Only re-add listeners if activePanelType changes

  // Helper function to get translations
  const t = (key) => getTranslation(selectedLanguage, key);

  const handleSearch = (searchTerm) => {
    setSearchHighlight(searchTerm);
    setSearchDirection(null);
  };

  const handleSearchNavigate = (direction) => {
    setSearchDirection(direction);
  };

  return (
    <Router basename="/modular">
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/"
          element={
            <ProtectedRoute>
              <div className={`App ${activePanelType ? 'panel-open' : ''}`}>
                <div className="header">
                  <button 
                    className={`chat-panel-toggle ${isTrayOpen ? 'open' : ''}`}
                    onClick={() => setIsTrayOpen(!isTrayOpen)}
                  >
                    <span className="hamburger-icon">&#9776;</span>
                  </button>
                  
                  <a href="https://illuminantinc.com" target="_blank" rel="noopener noreferrer">
                    <img src="/logo.png" alt="Logo" className="header-logo" />
                  </a>

                  <div className="header-right">
                    <LanguageSelector
                      currentLanguage={selectedLanguage}
                      onLanguageChange={setSelectedLanguage}
                      isOpen={isLanguageMenuOpen}
                      toggleOpen={() => setIsLanguageMenuOpen(!isLanguageMenuOpen)}
                    />
                    
                    <button 
                      className={`settings-button ${activePanelType === 'settings' ? 'open' : ''}`}
                      onClick={handleSettingsPanelToggle}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="3" />
                        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
                      </svg>
                    </button>
                  </div>
                </div>

                <LeftTray
                  isOpen={isTrayOpen}
                  activePanelType={activePanelType}
                  onPanelChange={(panelType) => {
                    setActivePanelType(panelType);
                    if (!panelType) setIsTrayOpen(false);
                  }}
                />

                <ChatPanel
                  isOpen={activePanelType === 'chat'}
                  onClose={() => {
                    setActivePanelType(null);
                    setIsTrayOpen(false);
                  }}
                  savedChats={savedChats}
                  onLoadChat={handleLoadChat}
                  onDeleteChat={handleDeleteChat}
                  onNewChat={handleNewChat}
                  onSearch={handleSearch}
                  onSearchNavigate={handleSearchNavigate}
                  translations={t}
                />

                <SettingsPanel
                  isOpen={activePanelType === 'settings'}
                  onClose={() => {
                    setActivePanelType(null);
                    setIsTrayOpen(false);
                  }}
                  onExpertiseLevelChange={handleExpertiseLevelChange}
                  currentExpertiseLevel={expertiseLevel}
                  messages={messages}
                  onGenerateImage={() => setShowImageModal(true)}
                  currentLanguage={selectedLanguage}
                  onLanguageChange={setSelectedLanguage}
                  translations={t}
                  currentModel={selectedModel}          // Pass the selected model
                  onModelChange={setSelectedModel}      // Pass the handler to update model
                  selectedVoice={selectedVoice}
                  setSelectedVoice={setSelectedVoice}
                />

                <ConversationPanel
                  isOpen={activePanelType === 'conversation'}
                  onClose={() => {
                    setActivePanelType(null);
                    setIsTrayOpen(false);
                  }}
                  onSend={handleSend}
                  selectedVoice={selectedVoice}
                  isProcessing={isProcessing}
                  translations={t}
                />

                <div className="chat-container">
                  <ChatHistory 
                    messages={messages} 
                    setMessages={setMessages}
                    sendButtonVisible={sendButtonVisible}
                    suggestions={isProcessing ? [] : suggestions}
                    onSuggestionClick={handleSuggestionClick}
                    isProcessing={isProcessing}
                    translations={t}
                    selectedVoice={selectedVoice}
                    searchHighlight={searchHighlight}
                    searchDirection={searchDirection}
                  />
                </div>
                <MessageInput 
                  ref={messageInputRef}
                  onSend={handleSend}
                  onImageUpload={handleImageUpload}
                  onSendButtonVisibilityChange={handleSendButtonVisibility}
                  messages={messages.filter(msg => msg.visible)}
                  onSuggestionsUpdate={setSuggestions}
                  placeholder={t('inputPlaceholder')}
                  style={{ display: activePanelType === 'conversation' ? 'none' : 'block' }}
                />
                {showImageModal && (
                  <Modal onClose={() => setShowImageModal(false)}>
                    <ImagePromptModal
                      onSubmit={handleImagePromptSubmit}
                      onCancel={() => setShowImageModal(false)}
                    />
                  </Modal>
                )}
                {showChatHistoryModal && (
                  <Modal onClose={() => setShowChatHistoryModal(false)}>
                    <ChatHistoryModal
                      savedChats={savedChats}
                      onLoadChat={handleLoadChat}
                      onClose={() => setShowChatHistoryModal(false)}
                      onDeleteChat={handleDeleteChat}
                    />
                  </Modal>
                )}
              </div>
            </ProtectedRoute>
          }
        />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    </Router>
  );
};

export default App;
